import { DOCK_CAMERA_LABELS } from '@/lib/dock-camera';
import type { LaunchMode } from '@/components/dashboard/DockBayRow';

/** Which drone the operator picked in a bay, and which launch workflow they
 *  asked for. Set only by an explicit click on a bay row's launch action. */
export interface LaunchIntent {
  droneId: string;
  mode: LaunchMode;
}

/**
 * May the operator move the waypoints currently drawn on the map?
 *
 * Only where the drawn route is what will actually fly. In `mission` mode it is NOT: since
 * #379 the dock endpoint routes a launch carrying a `mission_id` through the shared launch
 * service, which flies the route STORED against the mission and never reads the `waypoints`
 * the card sends. The plan on screen there is a rendering of the saved mission, not an input.
 *
 * Leaving it draggable let an operator pull a waypoint off a tree line, watch the review card
 * recompute its distance and EST, press LAUNCH — and the aircraft flew the original route.
 * The map showed one plan and the airframe flew another, which is the one thing a launch
 * display may never do. Editing a saved mission's route is the missions page's job.
 *
 * `takeoff` / `point` / `route` stay editable: those branches send their waypoints and the
 * server flies them verbatim, so a drag there is a real edit to a real plan.
 */
export function isDockPlanEditable(
  canDispatch: boolean,
  selectedDockId: string | null,
  launchIntent: LaunchIntent | null,
  waypointCount: number,
): boolean {
  if (!canDispatch || selectedDockId === null || waypointCount === 0) return false;
  return launchIntent?.mode !== 'mission';
}

/**
 * Segments of the drone details workspace, split by tempo: LIVE is everything
 * on the 1 Hz ticker, FEED is the polled stream registry. FEED used to carry
 * the picture too; video moved out to a floating window over the map, and what
 * stays here is the numbers that describe a picture rather than the picture.
 *
 * There was a third, the static registry row. It left for a row expansion on
 * the fleet table in #505 slice 11 (D53): nothing on it changes during a sortie,
 * and two segments are what make the panel comfortable at its 320 px default
 * rather than cramped - three tabs are what set the 288 px minimum in the first
 * place.
 */
export type DetailsSegment = 'live' | 'feed';

/**
 * A camera picture floating over the map, identified by WHAT IT SHOWS.
 *
 * Never by what is selected. The aircraft or dock a window points at is a
 * property of the window, not of whatever the operator happens to have clicked
 * since — a window open on dock A keeps showing dock A when dock B is picked.
 *
 * Clicking an aircraft on the map DOES open one (`FOLLOW_SELECTION_CAMERA`),
 * which is the one window that is not opened by name. It is therefore the one
 * window the next map click may take away, and `pinned` is how the operator
 * stops it: see that field. Every other window still closes only from its own X.
 *
 * `cameraId` is `SINGLE_CAMERA` for an aircraft that registered one unnamed
 * feed, so the open-set works identically whether or not cameras are named.
 * `cam` is the dock's camera slot — 1 looks outward, 2 into the bay (see
 * `lib/dock-camera`, field-corrected 2026-08-10).
 *
 * `callsign` is the name AT THE MOMENT IT WAS OPENED, and only a fallback: the
 * live name always wins. It exists because the sources a window resolves
 * against can stop answering while the window is still playing — the aircraft
 * list is site-filtered, and the dock registry is fetched once with no retry —
 * and a live picture the operator cannot name is worse than a stale name. Not
 * part of the window's identity: renaming a dock must not open a second window.
 */
export type CameraWindow =
  | {
      kind: 'drone';
      droneId: string;
      cameraId: string;
      callsign: string | null;
      /**
       * The operator asked for this window to STAY.
       *
       * Clicking an aircraft on the map opens its picture now, so one window
       * follows the selection: the next map click takes the previous aircraft's
       * window away with it. That is right for a window nobody asked for by
       * name, and wrong for one the operator is watching on purpose - a tray
       * approach they clicked away from to check another aircraft.
       *
       * The pin is how they say which. Pinned, the window outlives every
       * selection and closes only from its own X, which is the behaviour every
       * camera window had before the map click started opening them.
       *
       * A window popped out by hand from FEED is born pinned: pressing a key
       * that says POP OUT is already the operator naming this picture. Only the
       * map click's own window starts transient.
       *
       * NOT part of the window's identity - `cameraWindowKey` ignores it, so
       * pinning never opens a second window of the same camera.
       */
      pinned: boolean;
    }
  | { kind: 'dock'; dockId: string; cam: 1 | 2; callsign: string | null };

/**
 * How many pictures may float at once.
 *
 * Windows survive the selection now, so they accumulate: an operator clicking
 * through eight docks would otherwise be running sixteen HLS players against a
 * browser that has a map to draw at the same time. Four is two docks' worth of
 * cameras, or a tray approach plus the nose camera of the aircraft flying it —
 * the realistic ceiling for one operator's attention.
 *
 * The overflow is REFUSED, never evicted. Silently closing a picture someone is
 * watching is the exact failure this whole model exists to remove.
 */
export const MAX_CAMERA_WINDOWS = 4;

/** Stable identity for a window — the React key, and the key its remembered
 *  screen position is filed under. */
export function cameraWindowKey(window: CameraWindow): string {
  return window.kind === 'drone'
    ? `drone:${window.droneId}:${window.cameraId}`
    : `dock:${window.dockId}:${window.cam}`;
}

export function sameCameraWindow(a: CameraWindow, b: CameraWindow): boolean {
  return cameraWindowKey(a) === cameraWindowKey(b);
}

/**
 * The cameras of ONE aircraft that are currently in a window.
 *
 * The placement question — is this camera's picture in a window, or on the
 * stage — is only ever asked about the aircraft on screen, so every consumer
 * (the FEED segment, the manual stage, `stageCamera`) receives the plain
 * `string[]` it always did while the storage carries a drone id.
 *
 * The keys are the keys the WINDOWS were opened under, which is not always a
 * camera's own name: the map click's window carries `SINGLE_CAMERA`, meaning
 * the aircraft's default camera. `stageCamera.resolveCameraKey` is what turns
 * that back into a real camera, and every consumer goes through it.
 */
export function poppedCamerasFor(windows: CameraWindow[], droneId: string | null): string[] {
  if (droneId === null) return [];
  return windows.flatMap((w) => (w.kind === 'drone' && w.droneId === droneId ? [w.cameraId] : []));
}

/** True when opening one more window would exceed the cap — the caller's cue to
 *  say so out loud. The reducer refuses regardless; this exists so the refusal
 *  reaches the operator instead of being a press that does nothing. */
export function cameraWindowsFull(windows: CameraWindow[]): boolean {
  return windows.length >= MAX_CAMERA_WINDOWS;
}

/**
 * What a window is called in a sentence about the open set.
 *
 * The toast that refuses the one past the cap has to say which four are in the
 * way, because the operator cannot count windows that are stacked or dragged off
 * the visible map. `liveCallsign` is the name the WINDOW is currently showing -
 * passed in rather than resolved here, so the toast and the title bar cannot
 * disagree; a window listed under an id that appears on no title bar defeats the
 * whole point of naming them.
 */
export function cameraWindowName(window: CameraWindow, liveCallsign: string | null): string {
  const who =
    liveCallsign
    ?? window.callsign
    ?? (window.kind === 'drone' ? window.droneId : window.dockId).slice(-6).toUpperCase();
  // INTERNAL / EXTERNAL, never CAM1 / CAM2. Every other surface names these
  // slots that way, and `lib/dock-camera` records that the numbering was
  // field-corrected in 2026-08 because the original mapping was INVERTED - so
  // an operator reasoning "cam1 must be the internal one" gets it backwards.
  // This message exists solely to say which window to go and close.
  return window.kind === 'drone'
    ? who
    : `${who} ${DOCK_CAMERA_LABELS[window.cam].toUpperCase()}`;
}

/**
 * The cascade step a window opening now should take: the lowest one no window
 * already open is holding.
 *
 * NOT the position in the list. List indices are reused the moment a window
 * closes, while the windows that stay put keep the screen position their old
 * index gave them — so the next window to open inherits an index whose place on
 * screen is still occupied, and lands exactly on top of it. Open ALPHA, open
 * BRAVO, close ALPHA, open anything: the newcomer takes index 1, which is where
 * BRAVO is still sitting.
 *
 * A slot is freed only by the window holding it closing, so a free slot is free
 * on screen too. `held` is the caller's record of slot per open window.
 */
export function freeCascadeSlot(held: Record<string, number>): number {
  const taken = new Set(Object.values(held));
  let slot = 0;
  while (taken.has(slot)) slot += 1;
  return slot;
}

export interface SelectionState {
  selectedDroneId: string | null;
  selectedDockId: string | null;
  /** Drone details workspace (right side). Replaces the old video-only panel. */
  detailsOpen: boolean;
  detailsSegment: DetailsSegment;
  /**
   * Drawer peeked shut, below `lg` only — the panel is still open, its body is
   * just not drawn, so the operator can see the map underneath it.
   *
   * This lives here rather than inside DroneWorkspace because "open" and
   * "collapsed" are two halves of one question and only the reducer sees both.
   * As component state it produced a dead control: the chip's exception pill
   * dispatches OPEN_DETAILS, which is a no-op when the panel is already open,
   * so tapping a red pill on a collapsed drawer did nothing at all. Every route
   * that asks for the panel now un-collapses it as part of the same action.
   */
  detailsCollapsed: boolean;
  /** Every camera picture the operator has pulled out into a floating window —
   *  aircraft cameras popped out of the details panel, and dock cameras opened
   *  from the dock sidebar, in ONE list.
   *
   *  For the aircraft half this is also the whole placement model. Video plays
   *  in a window or on the stage and nowhere else — the details panel draws no
   *  picture — so a camera listed here is a camera the stage will not draw:
   *  two players on one stream is double the bandwidth and CPU for the same
   *  picture. Nothing else is stored.
   *
   *  A SET, not a boolean: an airframe carries more than one camera (the
   *  SkyPulse has a nose camera and a down camera, and a tray approach is
   *  watched on the down one) and the operator opens the ones they want over
   *  the map, reading the numbers for the rest in the panel.
   *
   *  Each entry names its own source. That is the whole point of the shape: the
   *  list used to hold bare camera ids and lean on `selectedDroneId` to say
   *  whose they were, which meant every selection change had to empty it — an
   *  operator watching a tray approach lost the picture by clicking a dock. A
   *  window now outlives the selection that opened it and closes only from its
   *  own X, and a window left open on dock A keeps showing dock A when dock B
   *  is selected instead of silently repointing at it.
   *
   *  ONE list rather than two so the cascade offset can be a single index: a
   *  dock window and an aircraft window used to open at index 0 each and land
   *  exactly on top of one another. Capped at `MAX_CAMERA_WINDOWS`. */
  cameraWindows: CameraWindow[];
  /** Drone row expanded in DRONE BAYS — at most one at a time. */
  selectedBayDroneId: string | null;
  launchIntent: LaunchIntent | null;
  /**
   * The missions sheet occupies the right column. One column, three claimants
   * (missions / drone workspace / dock sheet): this flag takes display
   * precedence when true, and every action that brings a drone or dock sheet
   * into view clears it — the tab model, not a stack. Selecting a drone or
   * dock on the map WITHOUT opening its sheet leaves the panel alone, so an
   * operator can keep the mission queue up while working aircraft on the map.
   */
  missionsPanelOpen: boolean;
}

/** Stands in for an aircraft that registered a single unnamed camera, so the
 *  open-set works identically whether or not cameras are named. */
export const SINGLE_CAMERA = '__single__';

export type SelectionAction =
  | { type: 'SELECT'; id: string }
  | { type: 'SELECT_DOCK'; id: string }
  | { type: 'CLOSE_DOCK' }
  | { type: 'SELECT_BAY_DRONE'; droneId: string | null }
  | { type: 'START_LAUNCH'; droneId: string; mode: LaunchMode }
  | { type: 'CANCEL_LAUNCH' }
  | { type: 'OPEN_DETAILS'; segment?: DetailsSegment }
  | { type: 'CLOSE_DETAILS' }
  | { type: 'SET_DETAILS_SEGMENT'; segment: DetailsSegment }
  | { type: 'TOGGLE_DETAILS_COLLAPSED' }
  // `droneId` is explicit rather than "whatever is selected": the window has to
  // outlive that selection, so the aircraft is recorded at the moment of the
  // press.
  | { type: 'POP_OUT_VIDEO'; droneId: string; cameraId?: string; callsign?: string | null }
  // The map click's own window: show this aircraft's default camera, and take
  // the previous aircraft's transient window away with it.
  | { type: 'FOLLOW_SELECTION_CAMERA'; droneId: string; callsign?: string | null }
  // Flip one drone window between "stays" and "the next map click may take it".
  | { type: 'TOGGLE_CAMERA_WINDOW_PIN'; window: CameraWindow }
  | { type: 'DOCK_VIDEO'; droneId: string; cameraId?: string }
  | { type: 'OPEN_DOCK_CAMERA'; dockId: string; cam: 1 | 2; callsign?: string | null }
  | { type: 'CLOSE_DOCK_CAMERA'; dockId: string; cam: 1 | 2 }
  // Paint order is list order, so "bring to front" is "move to the end".
  | { type: 'RAISE_CAMERA_WINDOW'; window: CameraWindow }
  | { type: 'OPEN_MISSIONS_PANEL' }
  | { type: 'CLOSE_MISSIONS_PANEL' };

export const initialSelection: SelectionState = {
  selectedDroneId: null,
  selectedDockId: null,
  detailsOpen: false,
  detailsSegment: 'live',
  detailsCollapsed: false,
  cameraWindows: [],
  selectedBayDroneId: null,
  launchIntent: null,
  missionsPanelOpen: false,
};

/**
 * Add a window, unless it is already open or the cap is reached.
 *
 * Opening an already-open camera is a no-op rather than a duplicate window — a
 * second copy of the same feed is never what was meant, and it would cost a
 * second decode of the same stream.
 *
 * The cap is enforced HERE as well as at the press, so no caller can grow the
 * set past it by forgetting to ask. A refused open is silent from the reducer's
 * side; saying so is the UI's job (see `cameraWindowsFull`).
 */
function openCameraWindow(state: SelectionState, window: CameraWindow): SelectionState {
  if (state.cameraWindows.some((w) => sameCameraWindow(w, window))) return state;
  if (cameraWindowsFull(state.cameraWindows)) return state;
  return { ...state, cameraWindows: [...state.cameraWindows, window] };
}

/**
 * Every window except the transient one belonging to a DIFFERENT aircraft.
 *
 * The map click opens one window that follows the selection, so the previous
 * aircraft's has to go when the next click lands - otherwise "one window
 * follows the selection" is just "a window per aircraft clicked", which is the
 * accumulation the cap exists to bound.
 *
 * Three things survive on purpose: every pinned window (the operator said so),
 * every dock window (it never followed a selection in the first place), and the
 * incoming aircraft's own window, pinned or not - it is about to be the one the
 * selection points at, and tearing it down to rebuild it would restart a player
 * that is already showing the right picture.
 */
export function dropTransientWindows(
  windows: CameraWindow[],
  keepDroneId: string | null,
): CameraWindow[] {
  return windows.filter(
    (w) => w.kind !== 'drone' || w.pinned || w.droneId === keepDroneId,
  );
}

function closeCameraWindow(state: SelectionState, window: CameraWindow): SelectionState {
  return {
    ...state,
    cameraWindows: state.cameraWindows.filter((w) => !sameCameraWindow(w, window)),
  };
}

export function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'SELECT': {
      const next = action.id || null;
      // Clearing drone selection (background map click — DroneMap.tsx's
      // handleMapClick and the selection chip's ✕ are the callers with an empty
      // id) must not disturb an
      // open Dock Sidebar or its in-progress bay/launch workflow. An explicit
      // drone pick is intentional navigation and still replaces dock context.
      // Open camera windows are deliberately NOT cleared here, the map click's
      // own transient one included. They name their own aircraft, so
      // deselecting one does not orphan them — and an operator who dismissed
      // the panel to see the map underneath still wants the picture over it.
      //
      // The transient window was briefly dropped here too, on the reading that
      // a background click is the operator clearing the picture. It is not: the
      // same empty id comes from the selection chip's ✕, which is the operator
      // decluttering the map, and both of them took away a picture the operator
      // had not dismissed and gave nothing back. ONE thing retires a transient
      // window — the next map pick, which replaces it — so a picture is never
      // lost without another arriving. Everything else is the window's own X.
      if (!next) {
        return { ...state, selectedDroneId: null, detailsOpen: false };
      }
      // The workspace stays open across an explicit drone switch so an operator
      // comparing two aircraft does not have to reopen it each time.
      return {
        ...state,
        selectedDroneId: next,
        selectedDockId: null,
        selectedBayDroneId: null,
        launchIntent: null,
        // Camera windows are NOT touched here, transient ones included.
        //
        // Retiring one belongs with the thing that replaces it, which is the
        // map pick alone (`FOLLOW_SELECTION_CAMERA`). Doing it on every SELECT
        // made the drone search, the fleet table, an alert row and the dock
        // sheet's GO TO DRONE all destroy the picture and put nothing in its
        // place: an operator watching a tray approach who typed a second
        // callsign into the search lost the approach and got no window for the
        // aircraft they looked up. A window outliving its selection is the
        // model every other window here already follows.
      };
    }
    case 'SELECT_DOCK': {
      const next = action.id || null;
      // Selecting a different dock drops any bay selection and launch intent —
      // both are scoped to the dock that was open.
      const sameDock = next !== null && next === state.selectedDockId;
      return {
        ...state,
        selectedDroneId: null,
        selectedDockId: next,
        detailsOpen: false,
        // Camera windows survive. Picking a dock used to empty them, on the
        // reasoning that a window has no aircraft to point at once the drone is
        // deselected — but each window names its aircraft, and clicking a dock
        // is very often exactly WHY the operator popped the picture out.
        selectedBayDroneId: sameDock ? state.selectedBayDroneId : null,
        launchIntent: sameDock ? state.launchIntent : null,
        // The dock sheet takes the column (tab model): a dock pick is explicit
        // navigation to that dock's workspace.
        missionsPanelOpen: next === null ? state.missionsPanelOpen : false,
      };
    }
    case 'CLOSE_DOCK':
      return { ...state, selectedDockId: null, selectedBayDroneId: null, launchIntent: null };
    case 'SELECT_BAY_DRONE':
      // Collapsing or switching rows abandons a launch workflow started from the
      // previous row, so the panel can never act on a stale drone.
      return {
        ...state,
        selectedBayDroneId: action.droneId,
        launchIntent:
          state.launchIntent && state.launchIntent.droneId === action.droneId
            ? state.launchIntent
            : null,
      };
    case 'START_LAUNCH':
      return {
        ...state,
        selectedBayDroneId: action.droneId,
        launchIntent: { droneId: action.droneId, mode: action.mode },
      };
    case 'CANCEL_LAUNCH':
      return { ...state, launchIntent: null };
    // Asking for the panel always shows the panel. A collapsed drawer is still
    // `detailsOpen`, so without the un-collapse here OPEN_DETAILS is a no-op and
    // the control that dispatched it reads as broken.
    case 'OPEN_DETAILS':
      return {
        ...state,
        detailsOpen: true,
        detailsSegment: action.segment ?? state.detailsSegment,
        detailsCollapsed: false,
        missionsPanelOpen: false,
      };
    case 'CLOSE_DETAILS':
      // Reset rather than remember: the next aircraft's panel opens shown.
      return { ...state, detailsOpen: false, detailsCollapsed: false };
    case 'SET_DETAILS_SEGMENT':
      return {
        ...state,
        detailsOpen: true,
        detailsSegment: action.segment,
        detailsCollapsed: false,
        missionsPanelOpen: false,
      };
    case 'TOGGLE_DETAILS_COLLAPSED':
      return { ...state, detailsCollapsed: !state.detailsCollapsed };
    case 'OPEN_MISSIONS_PANEL':
      // Takes the column by precedence alone — drone/dock selection and an open
      // workspace survive underneath, so the DRONE/DOCK tabs lead straight back.
      return { ...state, missionsPanelOpen: true };
    case 'CLOSE_MISSIONS_PANEL':
      return { ...state, missionsPanelOpen: false };
    case 'POP_OUT_VIDEO':
      return openCameraWindow(state, {
        kind: 'drone',
        droneId: action.droneId,
        cameraId: action.cameraId ?? SINGLE_CAMERA,
        callsign: action.callsign ?? null,
        // Pressing a key that says POP OUT is the operator naming this picture,
        // so it is never the one the next map click takes away.
        pinned: true,
      });
    case 'FOLLOW_SELECTION_CAMERA': {
      const kept = dropTransientWindows(state.cameraWindows, action.droneId);
      const existing = kept.find((w) => w.kind === 'drone' && w.droneId === action.droneId);
      // Already showing this aircraft - pinned from an earlier session, or the
      // same aircraft clicked twice. Raise it rather than open a second window
      // or restart the player that is already painting the right picture.
      if (existing) {
        return {
          ...state,
          cameraWindows: [...kept.filter((w) => w !== existing), existing],
        };
      }
      // The cap still binds. The refusal is silent from here; saying so is the
      // caller's job, exactly as it is for every other open (`cameraWindowsFull`).
      if (cameraWindowsFull(kept)) return { ...state, cameraWindows: kept };
      return {
        ...state,
        cameraWindows: [
          ...kept,
          {
            kind: 'drone',
            droneId: action.droneId,
            // The aircraft's DEFAULT camera - `CopVideoWindow` resolves an
            // absent camera id to the first registered stream, which is the
            // same camera the stage would draw. A click on the map is not a
            // choice of camera; choosing one is what FEED's per-camera pop-out
            // keys are for.
            cameraId: SINGLE_CAMERA,
            callsign: action.callsign ?? null,
            pinned: false,
          },
        ],
      };
    }
    case 'TOGGLE_CAMERA_WINDOW_PIN':
      return {
        ...state,
        cameraWindows: state.cameraWindows.map((w) =>
          w.kind === 'drone' && sameCameraWindow(w, action.window)
            ? { ...w, pinned: !w.pinned }
            : w,
        ),
      };
    case 'DOCK_VIDEO': {
      // Close the window, which is how the picture goes back to the STAGE: the
      // stage draws the first camera not in a window, so leaving this list IS
      // the transition. The window's dock button and its X do the same thing to
      // this reducer; only the window decides which of them to offer, from
      // whether there is a stage waiting (`stageTakesBack`).
      //
      // No cameraId docks every camera OF THAT AIRCRAFT — the "bring it all
      // back" verb on the manual stage. It is scoped to one drone on purpose:
      // another aircraft's window is not this aircraft's to close.
      if (action.cameraId === undefined) {
        return {
          ...state,
          cameraWindows: state.cameraWindows.filter(
            (w) => !(w.kind === 'drone' && w.droneId === action.droneId),
          ),
        };
      }
      return closeCameraWindow(state, {
        kind: 'drone',
        droneId: action.droneId,
        cameraId: action.cameraId,
        // Identity only — `cameraWindowKey` reads neither of these, so the
        // window that closes is whichever one has this drone and this camera,
        // whatever it was called and however it was pinned.
        callsign: null,
        pinned: false,
      });
    }
    case 'RAISE_CAMERA_WINDOW': {
      // A window resized to most of the viewport can bury another one whole,
      // and the buried one's X goes with it — so the operator needs a way back
      // that is not "close the thing on top". Any press inside a window raises
      // it; the dock sidebar's key for an already-open camera raises it too,
      // since "show me it" is what pressing an open camera means.
      const index = state.cameraWindows.findIndex((w) => sameCameraWindow(w, action.window));
      // Already on top, or gone. Returning `state` keeps this identity-stable
      // under a press-per-pointerdown, which is what it is wired to.
      if (index === -1 || index === state.cameraWindows.length - 1) return state;
      const raised = state.cameraWindows[index];
      return {
        ...state,
        cameraWindows: [...state.cameraWindows.filter((_, i) => i !== index), raised],
      };
    }
    case 'OPEN_DOCK_CAMERA':
      return openCameraWindow(state, {
        kind: 'dock',
        dockId: action.dockId,
        cam: action.cam,
        callsign: action.callsign ?? null,
      });
    case 'CLOSE_DOCK_CAMERA':
      return closeCameraWindow(state, {
        kind: 'dock',
        dockId: action.dockId,
        cam: action.cam,
        callsign: null,
      });
    default:
      return state;
  }
}

/**
 * Quick dispatch's own state. "Is the map armed to pick a target" is NOT here —
 * that is the COP's one map mode (`lib/map-mode.ts`), because it has to be
 * arbitrated against every other armed interaction. This reducer owns the
 * panel and the coordinates a pick produced.
 */
export interface DispatchState {
  dispatchOpen: boolean;
  injectedCoords: { latitude: number; longitude: number } | null;
  /**
   * Pre-fills the dialog's required description, so answering a trigger never
   * stops on "enter a short description" - the founder's flow is choose
   * altitude, choose drone, send, with no text step. `null` for the plain
   * map-click / right-click dispatch, which keeps asking the operator to type
   * one - that flow has no source to draw a description from.
   */
  injectedDescription: string | null;
}

export type DispatchAction =
  | { type: 'SET_OPEN'; open: boolean }
  | { type: 'PICK_DONE'; latitude: number; longitude: number }
  | { type: 'REQUEST_HERE'; latitude: number; longitude: number; description?: string | null };

export const initialDispatch: DispatchState = {
  dispatchOpen: false,
  injectedCoords: null,
  injectedDescription: null,
};

export function dispatchReducer(state: DispatchState, action: DispatchAction): DispatchState {
  switch (action.type) {
    case 'SET_OPEN':
      // Closing SPENDS the injected target. Without this the coordinates
      // outlive the dialog, and re-opening from the same point reads as
      // "unchanged" inside QuickDispatchDialog's syncProps - neither of its two
      // branches fires - so the operator gets a blank target on a point they
      // just clicked. Harmless while the only sources were a fresh map pick and
      // a right-click, which rarely repeat a coordinate exactly; guaranteed the
      // moment a trigger point is clickable twice.
      //
      // Safe against the pick flow: PICK_DONE fires while the dialog is OPEN
      // (the dialog owns the "Pick target on map" button and stays mounted and
      // open throughout), so a pick never rides through a close.
      return action.open
        ? { ...state, dispatchOpen: true }
        : { ...state, dispatchOpen: false, injectedCoords: null, injectedDescription: null };
    case 'PICK_DONE':
      return {
        ...state,
        injectedCoords: { latitude: action.latitude, longitude: action.longitude },
      };
    case 'REQUEST_HERE':
      return {
        ...state,
        injectedCoords: { latitude: action.latitude, longitude: action.longitude },
        dispatchOpen: true,
        injectedDescription: action.description ?? null,
      };
    default:
      return state;
  }
}
